import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * The "Application URL" value of the overview card, built from the CMDB `url`
 * field.
 *
 * In ServiceNow that field is free text. Teams use it to list every endpoint
 * of an application (prod, dev, qa, regional hosts, API and admin consoles),
 * so a value can be a placeholder ("N/A", "TBC"), a bare domain, several
 * addresses separated by commas, semicolons or spaces, or text with an
 * address buried in it. `parseApplicationUrl` cleans that up; the component
 * shows the most production-looking address and puts the rest behind a
 * "Show more" button.
 */

interface ApplicationLink {
  /** Always an absolute http(s) URL, safe to put in an `href`. */
  href: string;
  /** The address as it was written in the CMDB (trimmed). */
  label: string;
}

interface ParsedApplicationUrl {
  /** Addresses to show, the one most likely to be production first. */
  links: ApplicationLink[];
  /** Plain text to show when there are no links; null means "N/A". */
  text: string | null;
}

// "N/A", "N\A", "na", "Non", "None", "Nil", "TBC", "To Come", "No URL",
// "Not applicable" (and misspellings such as "Not appicable") and so on.
// Compared after dropping everything that is not a letter.
const PLACEHOLDERS = new Set([
  'na',
  'non',
  'none',
  'nil',
  'tbc',
  'tbd',
  'tocome',
  'nourl',
]);

const isPlaceholder = (text: string): boolean => {
  const letters = text.toLowerCase().replace(/[^a-z]/g, '');
  return (
    letters === '' || PLACEHOLDERS.has(letters) || letters.startsWith('notapp')
  );
};

// A hostname with a dot and a 2+ letter ending, an optional port and path.
const BARE_DOMAIN =
  /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d{1,5})?(?:[/?#]\S*)?$/i;

// Host names that suggest a non-production environment. Matched against whole
// dot- or dash-separated parts, so "csint" or "device" do not match.
const NON_PROD_HOST =
  /(^|[.-])(dev|qa|test\d*|tst|uat|stage|staging|stg|sandbox|perftest|preprod|failsafe|sit)([.-]|$)/i;

const stripPunctuation = (token: string): string =>
  token.replace(/^[("'[<]+/, '').replace(/[)"'\]>.,:;]+$/, '');

/** An http(s) URL for the token, or null if it is not a web address. */
const toHref = (token: string): string | null => {
  let candidate: string;
  if (/^https?:\/\//i.test(token)) {
    candidate = token;
  } else if (BARE_DOMAIN.test(token)) {
    candidate = `https://${token}`;
  } else {
    return null;
  }
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url.href
      : null;
  } catch {
    return null;
  }
};

export function parseApplicationUrl(
  raw: string | null | undefined,
): ParsedApplicationUrl {
  const value = (raw ?? '').trim();
  if (isPlaceholder(value)) {
    return { links: [], text: null };
  }

  const seen = new Set<string>();
  const links: ApplicationLink[] = [];
  for (const piece of value.split(/[\s,;]+/)) {
    const label = stripPunctuation(piece);
    const href = label ? toHref(label) : null;
    if (href && !seen.has(href.toLowerCase())) {
      seen.add(href.toLowerCase());
      links.push({ href, label });
    }
  }

  if (links.length === 0) {
    // Not an address, e.g. "Databricks": show it as plain text, not a link.
    return { links, text: value.replace(/\s+/g, ' ') };
  }

  // Production-looking hosts first. The sort is stable, so the CMDB order is
  // kept within each group.
  const rank = (link: ApplicationLink) =>
    NON_PROD_HOST.test(new URL(link.href).hostname) ? 1 : 0;
  links.sort((a, b) => rank(a) - rank(b));

  return { links, text: null };
}

const toggleStyle: CSSProperties = {
  marginLeft: 8,
  padding: 0,
  border: 'none',
  background: 'none',
  color: 'var(--kone-blue, #1450F5)',
  font: 'inherit',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const linkStyle: CSSProperties = { overflowWrap: 'anywhere' };

const Link = ({ link }: { link: ApplicationLink }) => (
  <a
    href={link.href}
    target="_blank"
    rel="noopener noreferrer"
    style={linkStyle}
  >
    {link.label}
  </a>
);

/**
 * Renders only inline elements so it can sit inside the card's <p>.
 */
export function ApplicationUrl({ value }: { value?: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const parsed = useMemo(() => parseApplicationUrl(value), [value]);

  if (parsed.links.length === 0) {
    return <>{parsed.text ?? 'N/A'}</>;
  }

  const [main, ...others] = parsed.links;
  return (
    <>
      <Link link={main} />
      {others.length > 0 && (
        <button
          type="button"
          style={toggleStyle}
          aria-expanded={expanded}
          onClick={() => setExpanded(open => !open)}
        >
          {expanded ? 'Show less' : `Show ${others.length} more`}
        </button>
      )}
      {expanded &&
        others.map(link => (
          <span key={link.href} style={{ display: 'block', marginTop: 4 }}>
            <Link link={link} />
          </span>
        ))}
    </>
  );
}
